import React, { useState, useEffect, useRef, useCallback } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';

// ─── Constants ────────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS        = 100;  // 5 minute ceiling
const STORAGE_KEY      = 'visinvoke_pending_job'; // legacy - kept for queue compat
const RESULT_KEY       = 'generation_result';
const JOB_KEY          = 'generation_job';

// ─── GeneratingResult ─────────────────────────────────────────────────────────
export default function GeneratingResult() {
  const navigate         = useNavigate();
  const location         = useLocation();
  const { user, logout } = useAuth();

  // Job state
  const [jobId,           setJobId]           = useState(null);
  const [slug,            setSlug]            = useState('');
  const [status,          setStatus]          = useState('PENDING'); // PENDING | PROCESSING | COMPLETE | FAILED | NOT_FOUND
  const [imageUrl,        setImageUrl]        = useState(null);
  const [imageUrls,       setImageUrls]       = useState([]); // multi-image
  const [seed,            setSeed]            = useState(null);
  const [creditsRefunded, setCreditsRefunded] = useState(false);
  const [pollCount,       setPollCount]       = useState(0);
  const [elapsedSecs,     setElapsedSecs]     = useState(0);
  const [errorMsg,        setErrorMsg]        = useState('');
  const [selectedImgIdx,  setSelectedImgIdx]  = useState(0); // which image is displayed large

  // UI state
  const [copied,    setCopied]    = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const pollTimer  = useRef(null);
  const clockTimer = useRef(null);
  const startTime  = useRef(Date.now());

  const displayName  = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : '';
  const avatarLetter = (user?.first_name || user?.email || 'V')[0].toUpperCase();

  // Derived: the URL currently shown large
  const activeImageUrl = imageUrls.length > 0 ? imageUrls[selectedImgIdx] : imageUrl;

  // ── Quick Edit mode — called from QuickEditPage via router state ────────────
  useEffect(() => {
    const st = location.state;
    if (!st || st.mode !== 'quick_edit') return;

    // Clear router state so back-navigation does not re-trigger
    window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

    const idempotency_key = 'qe_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);

    setStatus('PROCESSING');
    setSlug(st.slug || '');

    API.post('/v1/quick-edit/generate', {
      slug:             st.slug,
      build_id:         st.build_id,
      source_image_id:  st.source_image_id,
      slider_positions: st.slider_positions || {},
      output_count:     st.output_count || 1,
      idempotency_key:  idempotency_key,
    })
      .then(res => {
        const data = res.data || {};
        if (data.status === 'COMPLETE' && data.image_url) {
          setStatus('COMPLETE');
          setImageUrl(data.image_url);
          const urls = data.image_urls && data.image_urls.length > 0 ? data.image_urls : [data.image_url];
          setImageUrls(urls);
          setSelectedImgIdx(0);
          setSeed(data.seed ?? null);
          clearInterval(clockTimer.current);
        } else {
          setStatus('FAILED');
          setCreditsRefunded(data.credits_refunded === true);
          setErrorMsg(data.error || 'Generation failed. Please try again.');
          clearInterval(clockTimer.current);
        }
      })
      .catch(err => {
        const data = err.response?.data || {};
        setStatus('FAILED');
        setCreditsRefunded(data.credits_refunded === true);
        setErrorMsg(data.error || 'Generation failed. Please try again.');
        clearInterval(clockTimer.current);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resolve job on mount ───────────────────────────────────────────────────
  useEffect(() => {
    // Priority 1: Check for sync COMPLETE result
    try {
      const resultRaw = localStorage.getItem(RESULT_KEY);
      if (resultRaw) {
        const result = JSON.parse(resultRaw);
        if (result.status === 'COMPLETE' && result.image_url) {
          setStatus('COMPLETE');
          setImageUrl(result.image_url);
          const urls = result.image_urls && result.image_urls.length > 0 ? result.image_urls : [result.image_url];
          setImageUrls(urls);
          setSelectedImgIdx(0);
          setSeed(result.seed ?? null);
          setSlug(result.slug || '');
          localStorage.removeItem(RESULT_KEY);
          return;
        }
      }
    } catch {}

    // Priority 2: Check for async queue job
    try {
      const jobRaw = localStorage.getItem(JOB_KEY);
      if (jobRaw) {
        const { job_id, slug: storedSlug } = JSON.parse(jobRaw);
        if (job_id) {
          setJobId(job_id);
          setSlug(storedSlug || '');
          return;
        }
      }
    } catch {}

    // Priority 3: Legacy key (visinvoke_pending_job)
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const { job_id, slug: storedSlug } = JSON.parse(stored);
        if (job_id) {
          setJobId(job_id);
          setSlug(storedSlug || '');
          return;
        }
      }
    } catch {}

    // No job found
    setStatus('NOT_FOUND');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Clock ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (status !== 'PENDING' && status !== 'PROCESSING') return;
    clockTimer.current = setInterval(() => {
      setElapsedSecs(Math.floor((Date.now() - startTime.current) / 1000));
    }, 1000);
    return () => clearInterval(clockTimer.current);
  }, [status]);

  // ── Clear localStorage on terminal states ──────────────────────────────────
  useEffect(() => {
    if (status === 'COMPLETE' || status === 'FAILED' || status === 'NOT_FOUND') {
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
    }
  }, [status]);

  // ── Polling ────────────────────────────────────────────────────────────────
  const poll = useCallback(async (id, count) => {
    if (count >= MAX_POLLS) {
      setStatus('FAILED');
      setErrorMsg('Generation timed out. Please retry.');
      clearInterval(clockTimer.current);
      return;
    }

    try {
      const res  = await API.get(`/api/jobs/${id}/status`);
      const data = res.data;

      setPollCount(count + 1);

      if (data.status === 'COMPLETE') {
        setStatus('COMPLETE');
        setImageUrl(data.image_url);
        const urls = data.image_urls && data.image_urls.length > 0 ? data.image_urls : [data.image_url];
        setImageUrls(urls);
        setSelectedImgIdx(0);
        setSeed(data.seed ?? null);
        clearInterval(clockTimer.current);
        return;
      }

      if (data.status === 'FAILED') {
        setStatus('FAILED');
        setCreditsRefunded(data.credits_refunded === true || data.refund === true);
        setErrorMsg('Generation failed.' + (data.credits_refunded || data.refund ? ' Your credit has been refunded.' : ''));
        clearInterval(clockTimer.current);
        return;
      }

      // Still PENDING or PROCESSING
      setStatus(data.status || 'PROCESSING');
      pollTimer.current = setTimeout(() => poll(id, count + 1), POLL_INTERVAL_MS);

    } catch (err) {
      // 401/403 handled globally by api.js interceptor
      const st = err.response?.status;
      if (st === 404) {
        setStatus('NOT_FOUND');
        setErrorMsg('Job not found. It may have expired.');
        clearInterval(clockTimer.current);
        return;
      }
      if (!st || (st !== 401 && st !== 403)) {
        // Network blip - retry
        pollTimer.current = setTimeout(() => poll(id, count + 1), POLL_INTERVAL_MS);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!jobId) return;
    poll(jobId, 0);
    return () => {
      clearTimeout(pollTimer.current);
      clearInterval(clockTimer.current);
    };
  }, [jobId, poll]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  function formatTime(secs) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  function formatSlug(sl) {
    if (!sl) return '';
    return sl.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
  }

  async function handleCopyUrl() {
    if (!activeImageUrl) return;
    try {
      await navigator.clipboard.writeText(activeImageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }

  function handleDownload() {
    if (!activeImageUrl) return;
    const suffix = imageUrls.length > 1 ? `-img${selectedImgIdx + 1}` : '';
    const a = document.createElement('a');
    a.href     = activeImageUrl;
    a.download = slug
      ? `visinvoke-${slug}-${jobId?.slice(0, 8)}${suffix}.png`
      : `visinvoke-${jobId?.slice(0, 8)}${suffix}.png`;
    a.target = '_blank';
    a.rel    = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function handleDownloadAll() {
    imageUrls.forEach((url, i) => {
      setTimeout(() => {
        const a = document.createElement('a');
        a.href     = url;
        a.download = slug
          ? `visinvoke-${slug}-${jobId?.slice(0, 8)}-img${i + 1}.png`
          : `visinvoke-${jobId?.slice(0, 8)}-img${i + 1}.png`;
        a.target = '_blank';
        a.rel    = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }, i * 300);
    });
  }

  function handleReturnToGenerator() {
    if (slug) {
      sessionStorage.setItem('active_slug', slug);
      navigate('/engine', { state: { slug } });
    } else {
      navigate('/generators');
    }
  }

  function handleCreateVariation() {
    handleReturnToGenerator();
  }

  function handleAnimate() {
    if (!jobId) return;
    navigate(`/animate?image_id=${jobId}`);
  }

  // ── Render: spinner ────────────────────────────────────────────────────────
  function renderSpinner() {
    const pct = Math.min(100, Math.round((pollCount / MAX_POLLS) * 100));
    return (
      <div style={s.centreCard}>
        <div style={s.spinnerRings}>
          <div style={s.ring1} />
          <div style={s.ring2} />
          <div style={s.ringCore}>⚡</div>
        </div>
        <div style={s.spinnerTitle}>Generating image...</div>
        <div style={s.spinnerSub}>This may take several seconds.</div>

        <div style={s.statRow}>
          <div style={s.statItem}>
            <div style={s.statLabel}>Elapsed</div>
            <div style={s.statVal}>{formatTime(elapsedSecs)}</div>
          </div>
          <div style={s.statDivider} />
          <div style={s.statItem}>
            <div style={s.statLabel}>Checks</div>
            <div style={s.statVal}>{pollCount}</div>
          </div>
          <div style={s.statDivider} />
          <div style={s.statItem}>
            <div style={s.statLabel}>Max wait</div>
            <div style={s.statVal}>5 min</div>
          </div>
        </div>

        <div style={s.progressTrack}>
          <div style={{ ...s.progressFill, width: `${pct}%` }} />
        </div>
        <div style={s.progressNote}>
          {pct < 10 ? 'Queuing your job...'
           : pct < 50 ? 'Generation in progress...'
           : pct < 85 ? 'Almost there...'
           : 'Finalising...'}
        </div>
      </div>
    );
  }

  // ── Render: complete ───────────────────────────────────────────────────────
  function renderComplete() {
    const multiImage = imageUrls.length > 1;
    return (
      <div style={s.resultGrid}>

        {/* Left - image */}
        <div style={s.imagePanel}>
          {!imgLoaded && (
            <div style={s.imgLoadingOverlay}>
              <div style={s.imgLoadSpinner} />
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, marginTop: 12 }}>
                Loading image...
              </span>
            </div>
          )}
          {activeImageUrl && (
            <img
              key={activeImageUrl}
              src={activeImageUrl}
              alt="Generated visual"
              style={{ ...s.generatedImg, display: imgLoaded ? 'block' : 'none' }}
              onLoad={() => setImgLoaded(true)}
            />
          )}
          {imgLoaded && (
            <div style={s.imgBadgeRow}>
              <span style={s.badgeComplete}>✓ Complete</span>
              {multiImage && (
                <span style={s.badgeSeed}>{selectedImgIdx + 1} / {imageUrls.length}</span>
              )}
              {seed != null && <span style={s.badgeSeed}>Seed {seed}</span>}
              <span style={s.badgeTime}>{formatTime(elapsedSecs)}</span>
            </div>
          )}

          {/* Thumbnail strip for multi-image */}
          {multiImage && (
            <div style={s.thumbStrip}>
              {imageUrls.map((url, i) => (
                <div
                  key={i}
                  style={{
                    ...s.thumb,
                    ...(i === selectedImgIdx ? s.thumbActive : {}),
                  }}
                  onClick={() => { setSelectedImgIdx(i); setImgLoaded(false); }}
                >
                  <img src={url} alt={`Result ${i + 1}`} style={s.thumbImg} />
                  <div style={s.thumbLabel}>{i + 1}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right - actions */}
        <div style={s.actionCard}>
          <div style={s.actionHeader}>
            <div style={s.actionHeaderIcon}>🎨</div>
            <div>
              <div style={s.actionTitle}>
                {imageUrls.length > 1 ? `${imageUrls.length} Images Generated` : 'Image Generated'}
              </div>
              {slug && <div style={s.actionSub}>{formatSlug(slug)}</div>}
            </div>
          </div>

          {jobId && (
            <div style={s.jobMeta}>
              <span style={s.jobMetaLabel}>Job</span>
              <span style={s.jobMetaVal}>{jobId.slice(0, 20)}...</span>
            </div>
          )}

          <div style={s.actionBtns}>
            <button style={s.btnPrimary} onClick={handleDownload}>
              ⬇ Download {multiImage ? `Image ${selectedImgIdx + 1}` : 'Image'}
            </button>
            {multiImage && (
              <button style={s.btnDownloadAll} onClick={handleDownloadAll}>
                ⬇ Download All {imageUrls.length} Images
              </button>
            )}
            <button style={s.btnAnimate} onClick={handleAnimate}>
              ▶ Animate This
            </button>
            <button
              style={{ ...s.btnSecondary, ...(copied ? s.btnCopied : {}) }}
              onClick={handleCopyUrl}
            >
              {copied ? '✓ Copied!' : '🔗 Copy Image URL'}
            </button>
            <button style={s.btnVariation} onClick={handleCreateVariation}>
              🔄 Create Variation
            </button>
            <button style={s.btnReturn} onClick={handleReturnToGenerator}>
              ← Return to Generator
            </button>
          </div>

          <div style={s.urlWarning}>
            Download your image to keep it permanently. Images are served via CDN and may not remain accessible indefinitely.
          </div>

          <div style={s.divider} />

          <div style={s.moreActions}>
            <button style={s.moreBtn} onClick={() => navigate('/generators')}>
              🐉 Browse Generators
            </button>
            <button style={s.moreBtn} onClick={() => navigate('/dashboard')}>
              📊 Dashboard
            </button>
          </div>
        </div>

      </div>
    );
  }

  // ── Render: failed ─────────────────────────────────────────────────────────
  function renderFailed() {
    return (
      <div style={s.centreCard}>
        <div style={s.failIcon}>⚠️</div>
        <div style={s.failTitle}>Generation Failed</div>
        <div style={s.failMsg}>{errorMsg || 'Something went wrong. Please try again.'}</div>
        {creditsRefunded && (
          <div style={s.refundBadge}>✓ Credit refunded to your account</div>
        )}
        <div style={s.failBtns}>
          <button style={s.btnPrimary} onClick={handleReturnToGenerator}>
            Retry Generation
          </button>
          <button style={s.btnReturn} onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Render: not found ──────────────────────────────────────────────────────
  function renderNotFound() {
    return (
      <div style={s.centreCard}>
        <div style={s.failIcon}>🔍</div>
        <div style={s.failTitle}>No Active Generation Job Found</div>
        <div style={s.failMsg}>
          No generation job was detected. Return to the generator and click Generate Image to start.
        </div>
        <div style={s.failBtns}>
          <button style={s.btnPrimary} onClick={() => navigate('/generators')}>
            Browse Generators
          </button>
          <button style={s.btnReturn} onClick={() => navigate('/dashboard')}>
            Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>

      <nav style={s.nav}>
        <div style={s.navLogo} onClick={() => navigate('/dashboard')}>Visinvoke</div>
        <div style={s.navRight}>
          <button style={s.navBtn} onClick={() => navigate('/generators')}>Generator Library</button>
          <button style={s.navBtn} onClick={() => navigate('/dashboard')}>Dashboard</button>
          <div style={s.avatar}>{avatarLetter}</div>
          <span style={s.navName}>{displayName}</span>
          <button style={s.logoutBtn} onClick={() => { logout(); navigate('/login'); }}>Sign Out</button>
        </div>
      </nav>

      <div style={s.container}>

        <div style={s.breadcrumb}>
          <span style={s.crumb} onClick={() => navigate('/generators')}>Generator Library</span>
          <span style={s.crumbSep}>{'>'}</span>
          {slug && (
            <>
              <span style={s.crumb} onClick={handleReturnToGenerator}>{formatSlug(slug)}</span>
              <span style={s.crumbSep}>{'>'}</span>
            </>
          )}
          <span style={s.crumbActive}>
            {status === 'COMPLETE'  ? 'Result'
             : status === 'FAILED' || status === 'NOT_FOUND' ? 'Error'
             : 'Generating...'}
          </span>
        </div>

        <div style={s.contentWrap}>
          {(status === 'PENDING' || status === 'PROCESSING') && renderSpinner()}
          {status === 'COMPLETE'  && renderComplete()}
          {status === 'FAILED'    && renderFailed()}
          {status === 'NOT_FOUND' && renderNotFound()}
        </div>

      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)',
    fontFamily: "'Segoe UI', sans-serif",
    color: '#fff',
  },
  nav: {
    background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '0 40px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', height: 60, position: 'sticky', top: 0, zIndex: 100,
  },
  navLogo: {
    fontWeight: 800, fontSize: 20,
    background: 'linear-gradient(90deg, #a78bfa, #f472b6)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', cursor: 'pointer',
  },
  navRight:  { display: 'flex', alignItems: 'center', gap: 12 },
  navBtn: {
    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
    color: '#ccc', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  avatar: {
    width: 34, height: 34, borderRadius: '50%',
    background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 14, color: '#fff', flexShrink: 0,
  },
  navName:   { color: 'rgba(255,255,255,0.9)', fontSize: 14, fontWeight: 600 },
  logoutBtn: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.6)', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  container:   { maxWidth: 1200, margin: '0 auto', padding: '32px 40px' },
  breadcrumb:  { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 28 },
  crumb:       { color: '#a78bfa', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' },
  crumbSep:    { color: 'rgba(255,255,255,0.3)', fontSize: 14 },
  crumbActive: { color: '#fff', fontSize: 14, fontWeight: 600 },
  contentWrap: { display: 'flex', justifyContent: 'center' },

  // Centred white card (spinner / fail / not-found)
  centreCard: {
    background: 'rgba(255,255,255,0.97)', borderRadius: 20,
    padding: '56px 48px', maxWidth: 500, width: '100%',
    boxShadow: '0 16px 48px rgba(0,0,0,0.25)',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 18, color: '#1a1a1a', textAlign: 'center',
  },

  // Nested spinning rings
  spinnerRings: { position: 'relative', width: 80, height: 80 },
  ring1: {
    position: 'absolute', inset: 0, borderRadius: '50%',
    border: '3px solid #f0f0f0', borderTopColor: '#8b5cf6',
    animation: 'visinvoke-spin 1s linear infinite',
  },
  ring2: {
    position: 'absolute', inset: 10, borderRadius: '50%',
    border: '3px solid #f0f0f0', borderTopColor: '#db2777',
    animation: 'visinvoke-spin 0.65s linear infinite reverse',
  },
  ringCore: {
    position: 'absolute', inset: 22,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
  },
  spinnerTitle: { fontSize: 21, fontWeight: 800, color: '#1a1a1a' },
  spinnerSub:   { fontSize: 14, color: '#6b7280', lineHeight: 1.6, maxWidth: 320 },

  statRow: {
    display: 'flex', alignItems: 'center',
    background: '#f9fafb', borderRadius: 10, overflow: 'hidden',
    border: '1px solid #e5e7eb', width: '100%',
  },
  statItem:   { flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' },
  statDivider:{ width: 1, height: 40, background: '#e5e7eb', flexShrink: 0 },
  statLabel:  { fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em' },
  statVal:    { fontSize: 16, fontWeight: 700, color: '#1a1a1a' },

  progressTrack: { width: '100%', height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' },
  progressFill: {
    height: '100%', borderRadius: 3,
    background: 'linear-gradient(90deg, #8b5cf6, #db2777)',
    transition: 'width 0.6s ease',
  },
  progressNote: { fontSize: 12, color: '#9ca3af' },

  // Result two-column layout
  resultGrid: {
    display: 'grid', gridTemplateColumns: '1fr 380px',
    gap: 24, width: '100%', maxWidth: 1060, alignItems: 'start',
  },
  imagePanel: {
    background: 'rgba(0,0,0,0.3)', borderRadius: 16, overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.1)', minHeight: 400,
    position: 'relative', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
  },
  imgLoadingOverlay: {
    width: '100%', minHeight: 400,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  },
  imgLoadSpinner: {
    width: 40, height: 40, borderRadius: '50%',
    border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#a78bfa',
    animation: 'visinvoke-spin 0.8s linear infinite',
  },
  generatedImg: { width: '100%', height: 'auto', display: 'block', borderRadius: 16 },
  imgBadgeRow: { position: 'absolute', bottom: 14, left: 14, display: 'flex', gap: 8, alignItems: 'center' },
  badgeComplete: {
    background: 'rgba(16,185,129,0.9)', color: '#fff',
    fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, backdropFilter: 'blur(4px)',
  },
  badgeSeed: {
    background: 'rgba(0,0,0,0.65)', color: 'rgba(255,255,255,0.8)',
    fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 20, backdropFilter: 'blur(4px)',
  },
  badgeTime: {
    background: 'rgba(0,0,0,0.65)', color: 'rgba(255,255,255,0.7)',
    fontSize: 11, padding: '4px 10px', borderRadius: 20, backdropFilter: 'blur(4px)',
  },

  // Thumbnail strip
  thumbStrip: {
    display: 'flex', gap: 8, padding: '12px 14px',
    background: 'rgba(0,0,0,0.4)', width: '100%', boxSizing: 'border-box',
    borderTop: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap',
  },
  thumb: {
    width: 72, height: 72, borderRadius: 8, overflow: 'hidden',
    border: '2px solid rgba(255,255,255,0.2)', cursor: 'pointer',
    position: 'relative', flexShrink: 0,
    transition: 'border-color 0.15s',
  },
  thumbActive: {
    border: '2px solid #a78bfa',
    boxShadow: '0 0 0 2px rgba(167,139,250,0.4)',
  },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  thumbLabel: {
    position: 'absolute', bottom: 2, right: 4,
    fontSize: 10, fontWeight: 700, color: '#fff',
    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
  },

  // Action card
  actionCard: {
    background: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: 28,
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
    display: 'flex', flexDirection: 'column', gap: 14, color: '#1a1a1a',
  },
  actionHeader:     { display: 'flex', alignItems: 'center', gap: 12 },
  actionHeaderIcon: { fontSize: 32 },
  actionTitle:      { fontWeight: 800, fontSize: 19, color: '#1a1a1a' },
  actionSub:        { fontSize: 13, color: '#6b7280', marginTop: 2 },

  jobMeta: {
    background: '#f9fafb', borderRadius: 8,
    padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center',
  },
  jobMetaLabel: { fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' },
  jobMetaVal:   { fontSize: 12, color: '#374151', fontFamily: 'monospace' },

  actionBtns: { display: 'flex', flexDirection: 'column', gap: 8 },
  btnPrimary: {
    ...glassSurface('#5B21B6', '#8b5cf6', '#a855f7'),
    borderRadius: 10,
    padding: '13px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%',
  },
  btnDownloadAll: {
    ...glassSurface('#4C1D95', '#7c3aed', '#a855f7'),
    borderRadius: 10,
    padding: '11px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%',
  },
  btnAnimate: {
    ...glassSurface('#0E7A50', '#16a34a', '#4ADE80'),
    borderRadius: 10, padding: '11px 0',
    fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%',
  },
  btnSecondary: {
    ...glassSurface('#4C1D95', '#7c3aed', '#a855f7'),
    borderRadius: 10, padding: '11px 0',
    fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%',
    transition: 'background 0.2s',
  },
  btnCopied: { background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' },
  btnVariation: {
    ...glassSurface('#9A3412', '#c2410c', '#FB923C'),
    borderRadius: 10, padding: '11px 0',
    fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%',
  },
  btnReturn: {
    ...glassSurface('#334155', '#64748B', '#94A3B8'),
    borderRadius: 10, padding: '10px 0',
    fontWeight: 600, fontSize: 13, cursor: 'pointer', width: '100%',
  },
  urlWarning: {
    background: '#fffbeb', border: '1px solid #fde68a',
    borderRadius: 8, padding: '10px 14px',
    fontSize: 12, color: '#92400e', lineHeight: 1.5,
  },
  divider:     { height: 1, background: '#f0f0f0' },
  moreActions: { display: 'flex', gap: 8 },
  moreBtn: {
    flex: 1, background: '#f9fafb', color: '#374151',
    border: '1px solid #e5e7eb', borderRadius: 8,
    padding: '9px 0', fontWeight: 600, fontSize: 12, cursor: 'pointer',
  },

  // Fail / not-found elements (inside centreCard)
  failIcon:   { fontSize: 52 },
  failTitle:  { fontWeight: 800, fontSize: 22, color: '#1a1a1a' },
  failMsg:    { fontSize: 14, color: '#6b7280', lineHeight: 1.6, maxWidth: 360 },
  refundBadge: {
    background: '#dcfce7', border: '1px solid #bbf7d0',
    color: '#166534', borderRadius: 8, padding: '10px 18px',
    fontSize: 13, fontWeight: 700,
  },
  failBtns: { display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 4 },
};

// Inject spinner keyframe once
if (typeof document !== 'undefined' && !document.getElementById('visinvoke-spin-kf')) {
  const st = document.createElement('style');
  st.id          = 'visinvoke-spin-kf';
  st.textContent = '@keyframes visinvoke-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(st);
}
